/**
 * A global wrapper service for making HTTP requests with middleware support.
 *
 * @module services/request
 */
import superagent from 'superagent'
import { xClientAttributesMiddleware } from './x-client-attributes/middleware'

/**
 * Middleware chain function that applies a series of middlewares to the request.
 *
 * @param {Array<Function>} middlewares - An array of middleware functions to apply.
 * @returns {Function} A new function that wraps the original request with the applied middlewares.
 */
const applyMiddlewares = (middlewares) => (req) =>
  middlewares.reduce((currentReq, middleware) => middleware(currentReq), req)

/**
 * Global wrapper for making HTTP requests with multiple middlewares support.
 *
 * @type {Object}
 */
const request = {
  /**
   * Send a GET request to the specified URL with x-client-attributes middleware applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  get: (url) => applyMiddlewares([xClientAttributesMiddleware])(superagent.get(url)),

  /**
   * Send a POST request to the specified URL with x-client-attributes middleware applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  post: (url) => applyMiddlewares([xClientAttributesMiddleware])(superagent.post(url)),

  /**
   * Send a PUT request to the specified URL with x-client-attributes middleware applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  put: (url) => applyMiddlewares([xClientAttributesMiddleware])(superagent.put(url)),

  /**
   * Send a PATCH request to the specified URL with x-client-attributes middleware applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  patch: (url) => applyMiddlewares([xClientAttributesMiddleware])(superagent.put(url)),

  /**
   * Send a DELETE request to the specified URL with x-client-attributes middleware applied.
   *
   * @param {string} url - The URL to send the request to.
   * @returns {superagent.Request} The SuperAgent request object.
   */
  delete: (url) => applyMiddlewares([xClientAttributesMiddleware])(superagent.delete(url)),
}

export default request
